import { TextField } from '@mui/material';
import './Hssr.module.scss';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useUnmount } from '../../common/useUnmount';
import unauthenticatedService from '../../services/unauthenticatedJsaService';
import { ErrorRequestModal } from '../Common/ErrorRequestModal';
import { Loading } from '../Common/Loading';
import styles from './Hssr.module.scss';
import { HssrRequestedAppModal } from './HssrRequestedAppModal';
import {
    PredefinedAnswer,
    DefaultHssrForm,
    GetInvalidAnswers,
    GetPipeline,
    GetYearsForAnswer2,
    HssrForm,
    IsDirtyArrayAnswer,
    IsDirtyValueAnswer,
    isEmptyString,
    isInvalidBloodPressure,
    SubmitResponse,
    ToggleItem,
    UnauthenticatedHssrFormProps,
    YesOrNoOption,
} from './UnauthenticatedHssrForm.metadata';

export const UnauthenticatedHssrForm = ({ memberData, onFormSubmitted, language }: UnauthenticatedHssrFormProps) => {
    const unmountSub = useUnmount();

    const [form, setForm] = useState<HssrForm>(DefaultHssrForm(memberData));
    const [invalidForm, setInvalidForm] = useState<boolean>(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        unmountSub(GetInvalidAnswers(form), (invalidAnswers) => {
            setInvalidForm(invalidAnswers.length > 0);
        });
    }, [form]);

    const onSubmitForm = () => {
        setLoading(true);
        const responseHandler = (response: SubmitResponse) => {
            const showModal = response.success ? setShowSuccessModal : setShowErrorModal;
            setLoading(false);
            showModal(true);
            onFormSubmitted();
        };
        const onError = (_: any) => {
            setLoading(false);
            setShowErrorModal(true);
        };
        unmountSub(unauthenticatedService.createHssrRequest$(form), {
            next: responseHandler,
            error: onError,
        });
    };

    const handleSubmittedHssrRequest = () => {
        setForm(DefaultHssrForm(memberData));
        window.location.href = 'https://members.alignmenthealthplan.com/';
        setShowSuccessModal(false);
    };
    const getDate = (value: string, fallbackValue: string) => {
        // captures a valid MM/YYYY **as you type it**
        const regex = /^0?$|^(?:0[1-9]|1[012]?)((?:(?:\/\d{0,4})?)?)?$/;
        const matchList = value.match(regex);

        return matchList && matchList?.length > 0
            ? matchList[0] // get the new valid input
            : fallbackValue; // fallback to previous valid input (default is '')
    };

    const getReading = (value: string, fallbackValue: string) => {
        // captures a valid  nnn/nn **as you type it** (e.g. 180/90)
        const regex = /^\d$|^\d{2}$|^\d{2}\/0?$|^\d{3}$|^\d{3}\/0?$|^\d{2}\/[0-9]?[0-9]?[0-9]$|^\d{3}\/[0-9]?[0-9]?[0-9]$/;
        const matchList = value.match(regex);

        return matchList && matchList?.length > 0
            ? matchList[0] // get the new valid input
            : fallbackValue; // fallback to previous valid input (default is '')
    };

    const colorectalScreeningOptions = [language.colonoscopy, language.stoolTest, language.cologuard, language.no, ''];

    const yesNoOptions = [language.yes, language.no, ''];

    return (
        <>
            {form.requiredFields.includes('answer1') && (
                <div className={'row ' + styles['hssr-form']}>
                    <p className='col-sm-12'>{language.breastCancerTitle}</p>
                    <FormControl className='col-sm-12 p-2' component='fieldset' variant='outlined'>
                        <InputLabel className='p-2' id='answer1-label'>
                            {' '}
                            {language.select}
                        </InputLabel>
                        <Select
                            id='answer1-options'
                            label={language.select}
                            value={form.answer1.response}
                            error={IsDirtyArrayAnswer('answer1', 'response', isEmptyString)(form)}
                            onChange={(e) => {
                                const newDirtyFields = ToggleItem(form.dirtyFields, 'answer1', e.target.value !== 'No');
                                const newAnswer: PredefinedAnswer<YesOrNoOption> = {
                                    year: '',
                                    month: '',
                                    response: e.target.value as YesOrNoOption,
                                };
                                const pipeline = GetPipeline(['dirtyFields', newDirtyFields], ['answer1', newAnswer]);
                                unmountSub(pipeline(form), setForm);
                            }}
                        >
                            {yesNoOptions
                                .filter((option) => !isEmptyString(option))
                                .map((option, i) => (
                                    <MenuItem key={`answer1-option-${option}-${i}`} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                    <FormControl className='col-sm-12 col-md-6 mt-2 p-2' component='fieldset' variant='outlined'>
                        <InputLabel className='p-2' id='answer1-year-label'>
                            &nbsp;{language.enterYear}
                        </InputLabel>
                        <Select
                            id='answer1-year-select'
                            label={language.enterYear}
                            value={form.answer1.year}
                            disabled={(['No', '']).includes(form.answer1.response)}
                            error={IsDirtyArrayAnswer('answer1', 'year', isEmptyString)(form)}
                            onChange={(e) => {
                                const newDirtyFields = ToggleItem(form.dirtyFields, 'answer1', true);
                                const newAnswer: PredefinedAnswer<string> = {
                                    ...form.answer1,
                                    year: e.target.value,
                                };
                                const pipeline = GetPipeline(['dirtyFields', newDirtyFields], ['answer1', newAnswer]);
                                unmountSub(pipeline(form), setForm);
                            }}
                        >
                            {['2022', '2023', '2024'].map((option, i) => (
                                <MenuItem key={`answer1-year-${option}-${i}`} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl className='col-sm-12 col-md-6 mt-2 p-2' component='fieldset' variant='outlined'>
                        <InputLabel className='p-2' id='answer1-month-label'>
                            &nbsp;{language.enterMonth}
                        </InputLabel>
                        <Select
                            id='answer1-month-select'
                            label={language.enterMonth}
                            value={form.answer1.month}
                            disabled={isEmptyString(form.answer1.year)}
                            error={IsDirtyArrayAnswer('answer1', 'month', isEmptyString)(form)}
                            onChange={(e) => {
                                const newDirtyFields = ToggleItem(form.dirtyFields, 'answer1', true);
                                const newAnswer: PredefinedAnswer<string> = {
                                    ...form.answer1,
                                    month: e.target.value,
                                };
                                const pipeline = GetPipeline(['dirtyFields', newDirtyFields], ['answer1', newAnswer]);
                                unmountSub(pipeline(form), setForm);
                            }}
                        >
                            {_.range(1, 13).map((monthNumber) => {
                                const stringValue = monthNumber.toString().padStart(2, '0');
                                const shouldRender = form.answer1.year === '2022' ? monthNumber > 9 : true;
                                return (
                                    shouldRender && (
                                        <MenuItem key={`answer1-month-${stringValue}`} value={stringValue}>
                                            {stringValue}
                                        </MenuItem>
                                    )
                                );
                            })}
                        </Select>
                    </FormControl>
                </div>
            )}
            {form.requiredFields.includes('answer2') && (
                <div className={'row ' + styles['hssr-form']}>
                    <p className='col-sm-12'>{language.colorectalTitle}</p>
                    <FormControl className={'col-sm-12 p-2'} component='fieldset' variant='outlined'>
                        <InputLabel id='answer2-label' className='p-2'>
                            &nbsp;{language.select}
                        </InputLabel>
                        <Select
                            id='answer2-options'
                            label={language.select}
                            value={form.answer2.response}
                            error={IsDirtyArrayAnswer('answer2', 'response', isEmptyString)(form)}
                            onChange={(e) => {
                                const newDirtyFields = ToggleItem(form.dirtyFields, 'answer2', e.target.value !== 'No');
                                const newAnswer: PredefinedAnswer<string> = {
                                    year: '',
                                    month: '',
                                    response: e.target.value as string,
                                };
                                const pipeline = GetPipeline(['dirtyFields', newDirtyFields], ['answer2', newAnswer]);
                                unmountSub(pipeline(form), setForm);
                            }}
                        >
                            {colorectalScreeningOptions
                                .filter((option) => !isEmptyString(option))
                                .map((option, i) => (
                                    <MenuItem key={`answer2-option-${option}-${i}`} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                    <FormControl className='mt-2 p-2 col-sm-12 col-md-6' component='fieldset' variant='outlined'>
                        <InputLabel className='p-2' id='answer2-year-label'>
                            &nbsp;{language.enterYear}
                        </InputLabel>
                        <Select
                            id='answer2-year-select'
                            label={language.enterYear}
                            disabled={(['No', '']).includes(form.answer2.response)}
                            value={form.answer2.year}
                            error={IsDirtyArrayAnswer('answer2', 'year', isEmptyString)(form)}
                            onChange={(e) => {
                                const newDirtyFields = ToggleItem(form.dirtyFields, 'answer2', true);
                                const newAnswer: PredefinedAnswer<string> = {
                                    ...form.answer2,
                                    year: e.target.value,
                                };
                                const pipeline = GetPipeline(['dirtyFields', newDirtyFields], ['answer2', newAnswer]);
                                unmountSub(pipeline(form), setForm);
                            }}
                        >
                            {GetYearsForAnswer2(form.answer2.response, language).map((option, i) => (
                                <MenuItem key={`answer2-year-${option}-${i}`} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl className='mt-2 p-2 col-sm-12 col-md-6' component='fieldset' variant='outlined'>
                        <InputLabel className='p-2' id='answer2-month-label'>
                            &nbsp;{language.enterMonth}
                        </InputLabel>
                        <Select
                            id='answer2-month-select'
                            label={language.enterMonth}
                            disabled={form.answer2.year === ''}
                            value={form.answer2.month}
                            error={IsDirtyArrayAnswer('answer2', 'month', isEmptyString)(form)}
                            onChange={(e) => {
                                const newDirtyFields = ToggleItem(form.dirtyFields, 'answer2', true);
                                const newAnswer: PredefinedAnswer<string> = {
                                    ...form.answer2,
                                    month: e.target.value,
                                };
                                const pipeline = GetPipeline(['dirtyFields', newDirtyFields], ['answer2', newAnswer]);
                                unmountSub(pipeline(form), setForm);
                            }}
                        >
                            {_.range(1, 13).map((monthNumber) => {
                                const stringValue = monthNumber.toString().padStart(2, '0');
                                return (
                                    <MenuItem key={`answer2-month-${stringValue}`} value={stringValue}>
                                        {stringValue}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                </div>
            )}
            {form.requiredFields.includes('answer3') && (
                <div className={'row ' + styles['hssr-form']}>
                    <p className='col-sm-12'>{language.bloodPressureTestTitle}</p>
                    <FormControl className='col-sm-12 p-2' component='fieldset' variant='outlined'>
                        <TextField
                            id='answer3-pressure'
                            label={language.bloodPressureEnterMostRecentReading}
                            value={form.answer3.response}
                            error={IsDirtyArrayAnswer('answer3', 'response', isInvalidBloodPressure)(form)}
                            onChange={({ target }) => {
                                const newValue = getReading(
                                    target.value,
                                    isEmptyString(target.value) ? '' : form.answer3.response,
                                );
                                const newDirtyFields = ToggleItem(form.dirtyFields, 'answer3', true);
                                const newAnswer: PredefinedAnswer<string> = {
                                    ...form.answer3,
                                    response: newValue,
                                };
                                const pipeline = GetPipeline(['dirtyFields', newDirtyFields], ['answer3', newAnswer]);
                                unmountSub(pipeline(form), setForm);
                            }}
                        />
                    </FormControl>
                    <FormControl className='col-sm-12 col-md-6 mt-2 p-2' component='fieldset' variant='outlined'>
                        <InputLabel className='p-2' id='answer3-year-label'>
                            &nbsp;{language.enterYear}
                        </InputLabel>
                        <Select
                            id='answer3-year-select'
                            label={language.enterYear}
                            value={form.answer3.year}
                            disabled={isEmptyString(form.answer3.response)}
                            error={IsDirtyArrayAnswer('answer3', 'year', isEmptyString)(form)}
                            onChange={(e) => {
                                const newDirtyFields = ToggleItem(form.dirtyFields, 'answer3', true);
                                const newAnswer: PredefinedAnswer<string> = {
                                    ...form.answer3,
                                    year: e.target.value,
                                };
                                const pipeline = GetPipeline(['dirtyFields', newDirtyFields], ['answer3', newAnswer]);
                                unmountSub(pipeline(form), setForm);
                            }}
                        >
                            {['2024'].map((option, i) => (
                                <MenuItem key={`answer3-year-${option}-${i}`} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl className='col-sm-12 col-md-6 mt-2 p-2' component='fieldset' variant='outlined'>
                        <InputLabel className='p-2' id='answer3-month-label'>
                            &nbsp;{language.enterMonth}
                        </InputLabel>
                        <Select
                            id='answer3-month-select'
                            label={language.enterMonth}
                            value={form.answer3.month}
                            disabled={isEmptyString(form.answer3.year)}
                            error={IsDirtyArrayAnswer('answer3', 'month', isEmptyString)(form)}
                            onChange={(e) => {
                                const newDirtyFields = ToggleItem(form.dirtyFields, 'answer3', true);
                                const newAnswer: PredefinedAnswer<string> = {
                                    ...form.answer3,
                                    month: e.target.value,
                                };
                                const pipeline = GetPipeline(['dirtyFields', newDirtyFields], ['answer3', newAnswer]);
                                unmountSub(pipeline(form), setForm);
                            }}
                        >
                            {_.range(1, 13).map((monthNumber) => {
                                const stringValue = monthNumber.toString().padStart(2, '0');
                                return (
                                    <MenuItem key={`answer3-month-${stringValue}`} value={stringValue}>
                                        {stringValue}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                </div>
            )}
            <div className={'row ' + styles['hssr-form']}>
                <p className='col-sm-12'>{language.needAnyHelp}</p>
                <FormControl className='col-sm-12 p-2' component='fieldset' variant='outlined'>
                    <InputLabel id='answer4-label'>&nbsp;{language.select}</InputLabel>
                    <Select
                        id='answer4-options'
                        label={language.select}
                        value={form.answer4}
                        error={IsDirtyValueAnswer('answer4', isEmptyString)(form)}
                        onChange={(e) => {
                            const newDirtyFields = ToggleItem(form.dirtyFields, 'answer4', true);
                            const pipeline = GetPipeline(['dirtyFields', newDirtyFields], ['answer4', e.target.value]);
                            unmountSub(pipeline(form), setForm);
                        }}
                    >
                        {yesNoOptions
                            .filter((option) => !isEmptyString(option))
                            .map((option, i) => (
                                <MenuItem key={`answer4-option-${option}-${i}`} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                    </Select>
                </FormControl>
            </div>
            <div className={'row ' + styles['hssr-form']}>
                <FormControl
                    className='col-sm-12 p-2 mt-3'
                    component='fieldset'
                    variant='outlined'
                    error={IsDirtyValueAnswer('answer5', (answer) => !answer)(form)}
                >
                    <FormControlLabel
                        sx={{ fontWeight: 'bold' }}
                        className={'align-items-start col-sm-12'}
                        labelPlacement={'end'}
                        label={language.iConfirm}
                        control={
                            <Checkbox
                                id='answer4-checkbox'
                                checked={form.answer5}
                                onChange={(e) => {
                                    const newDirtyFields = ToggleItem(form.dirtyFields, 'answer5', true);
                                    const pipeline = GetPipeline(
                                        ['dirtyFields', newDirtyFields],
                                        ['answer5', e.target.checked],
                                    );
                                    unmountSub(pipeline(form), setForm);
                                }}
                            />
                        }
                    />
                </FormControl>
            </div>
            <div className={'row ' + styles['hssr-form']}>
                <div className='col-lg text-center mt-5'>
                    <Button
                        className='button'
                        onClick={onSubmitForm}
                        variant='primary'
                        size='lg'
                        disabled={invalidForm}
                    >
                        {language.send}
                    </Button>
                </div>
                <HssrRequestedAppModal show={showSuccessModal} onHideModal={handleSubmittedHssrRequest} language={language} />
                <ErrorRequestModal
                    showErrorModal={showErrorModal}
                    handleCloseErrorModal={() => setShowErrorModal(false)}
                    text={language.submitError}
                />
                {loading && <Loading />}
            </div>
        </>
    );
};
